import type { PaginationType } from '../types/Pagination.types';
import { z } from 'zod';
import { MediaDtoSchema } from '../media/media.schema';
import { PaginationParamsSchema, PaginationSchema } from '../types/Pagination.types';

export const UserFreeSpinStateValues = {
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed',
  PENDING: 'pending',
  EXPIRED: 'expired',
} as const;

export type UserFreeSpinStateValuesType = (typeof UserFreeSpinStateValues)[keyof typeof UserFreeSpinStateValues];

export const UserFreeSpinStateSchema = z.enum([
  UserFreeSpinStateValues.PENDING,
  UserFreeSpinStateValues.IN_PROGRESS,
  UserFreeSpinStateValues.COMPLETED,
  UserFreeSpinStateValues.EXPIRED,
]);

export const UserFreeSpinGameProviderDtoSchema = z.object({
  name: z.string(),
}).describe('UserFreeSpinGameProviderDto');

export const UserFreeSpinGameDtoSchema = z.object({
  name: z.string(),
  cover: MediaDtoSchema.nullable().optional(),
  provider: UserFreeSpinGameProviderDtoSchema,
}).describe('UserFreeSpinGameDto');

export const FreeSpinPackGameDtoSchema = z.object({
  name: z.string(),
  cover: MediaDtoSchema.nullable().optional(),
}).describe('FreeSpinPackGameDto');

export const FreeSpinPackProviderDtoSchema = UserFreeSpinGameProviderDtoSchema.describe('FreeSpinPackProviderDto');

export const FreeSpinPackDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  betAmount: z.number(),
  numberOfBets: z.number(),
  type: z.enum(['gamification', 'marketing', 'support']),
  gameId: z.string(),
  providerId: z.string(),
  game: FreeSpinPackGameDtoSchema.optional(),
  provider: FreeSpinPackProviderDtoSchema.optional(),
  // ISO 8601 duration format (e.g., "P1DT2H3M4S", PT24H)
  duration: z.string().nullable(),
  deactivatedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
}).describe('FreeSpinPackDto');

export const UserFreeSpinWithPackDtoSchema = z.object({
  id: z.string(),
  packId: z.string(),
  betAmount: z.number(),
  expiresAt: z.string().datetime(),
  gameId: z.string(),
  game: UserFreeSpinGameDtoSchema.optional(),
  userId: z.string(),
  externalId: z.string().nullable(),
  state: UserFreeSpinStateSchema,
  createdAt: z.string().datetime(),
  pack: FreeSpinPackDtoSchema,
}).describe('UserFreeSpinWithPackDto');

export const UserFreeSpinWithPackListDtoSchema = PaginationSchema(UserFreeSpinWithPackDtoSchema).describe('UserFreeSpinWithPackListDto');

export const UserFreeSpinFiltersSchema = z.object({
  state: z.object({
    $notin: z.array(UserFreeSpinStateSchema),
  }).optional(),
}).optional().describe('UserFreeSpinFilters');

export const UserFreeSpinListParamsSchema = PaginationParamsSchema(UserFreeSpinFiltersSchema).optional().describe('UserFreeSpinListParams');

export const FreeSpinPackAssignedContextSchema = z.object({
  userId: z.string(),
  userFreeSpinId: z.string(),
  freeSpinPackId: z.string(),
  gameId: z.string(),
  expiresAt: z.string().datetime(),
}).describe('FreeSpinPackAssignedContext');

export const FreeSpinPackUnassignedContextSchema = FreeSpinPackAssignedContextSchema.omit({
  expiresAt: true,
}).describe('FreeSpinPackUnassignedContext');

export type UserFreeSpinListParams = z.infer<typeof UserFreeSpinListParamsSchema>;
export type UserFreeSpinWithPackDto = z.infer<typeof UserFreeSpinWithPackDtoSchema>;
export type UserFreeSpinWithPackListDto = PaginationType<UserFreeSpinWithPackDto>;
export type FreeSpinPackAssignedContext = z.infer<typeof FreeSpinPackAssignedContextSchema>;
export type FreeSpinPackUnassignedContext = z.infer<typeof FreeSpinPackUnassignedContextSchema>;
